import { createLogger } from "../../helper/logger.js";
import { initializeStore } from "../../store/mini-rx.store.js";
import { services } from "../../service-provider/ServiceRegistry.js";
import { pdRouterService } from "../../router/PdRouterService.js";
//#region src/components/pd-spa-helper/spa-config.ts
var configLogger = createLogger("SpaConfig");
var SYSTEM_PAGES = {
	login: {
		name: "login",
		menu: [],
		pattern: ["/login"],
		auth: false
	},
	unauthorized: {
		name: "unauthorized",
		menu: [],
		pattern: ["/unauthorized"],
		auth: false
	},
	notFound: {
		name: "not-found",
		menu: [],
		pattern: ["/*wildcard"],
		auth: false
	}
};
/**
* Singleton configuration manager for SPA settings.
* Handles initialization, route transformation, and page lookup.
*/
var SpaConfigManager = class {
	constructor() {
		this._state = null;
	}
	/**
	* Check if configuration has been initialized
	*/
	get isInitialized() {
		return this._state?.initialized ?? false;
	}
	/**
	* Get the current configuration state
	* @throws Error if not initialized
	*/
	get state() {
		if (!this._state) throw new Error("SPA Configuration not initialized. Call spaConfig.initialize() first.");
		return this._state;
	}
	/**
	* Initialize the SPA configuration.
	* This sets up the store, routes, and navigation.
	*
	* IMPORTANT: ServiceProvider must be initialized via services.initialize()
	* BEFORE calling this method.
	*
	* @param config - The application configuration
	*/
	initialize(config) {
		configLogger.debug("Initializing SPA configuration", config);
		if (!services.isInitialized) throw new Error("ServiceRegistry not initialized. Call services.initialize(provider) before spaConfig.initialize()");
		if (config.storeConfigParam) initializeStore(config.storeConfigParam);
		if (config.functionsConfig?.region) services.functions.setRegion(config.functionsConfig.region);
		const navigation = this._buildNavigationConfig(config.navigationConfigParam);
		const routes = this._transformRoutes(navigation);
		const routeMap = new Map(navigation.pages.map((p) => [p.name, p]));
		pdRouterService.configureRoutes(routes);
		this._state = {
			navigation,
			routes,
			routeMap,
			initialized: true,
			defaultLoaderStyle: config.defaultLoaderStyle,
			pwaUpdateConfig: config.pwaUpdateConfigParam
		};
		configLogger.debug("SPA configuration initialized successfully");
	}
	/**
	* Get a navigation page by route name
	*
	* @param routeName - The name of the route
	* @returns The navigation page configuration
	* @throws Error if page not found
	*/
	getNavigationPage(routeName) {
		const page = this.state.routeMap.get(routeName);
		if (!page) throw new Error(`Navigation page not defined for route: ${routeName}`);
		return page;
	}
	/**
	* Check if a route exists
	*
	* @param routeName - The name of the route
	*/
	hasRoute(routeName) {
		return this.state.routeMap.has(routeName);
	}
	/**
	* Get the navigation configuration
	*/
	getNavigationConfig() {
		return this.state.navigation;
	}
	/**
	* Check if login is included in the configuration
	* Safe to call even if not initialized (returns false)
	*/
	get includesLogin() {
		return this._state?.navigation.includeLogin ?? false;
	}
	/**
	* Get the route to redirect to after logout.
	* Defaults to "/" (home) if not configured.
	*/
	get logoutRedirect() {
		return this.state.navigation.logoutRedirect ?? "/";
	}
	/**
	* Get the default loader style for pd-loading-state.
	* Returns undefined if not configured (component default "spinner" applies).
	*/
	/**
	* PWA update flow configuration (dialog texts + service options).
	* Safe to call even if not initialized (returns undefined)
	*/
	get pwaUpdateConfig() {
		return this._state?.pwaUpdateConfig;
	}
	get defaultLoaderStyle() {
		return this._state?.defaultLoaderStyle;
	}
	/**
	* Reset the configuration (useful for testing)
	*/
	reset() {
		this._state = null;
		configLogger.debug("SPA configuration reset");
	}
	/**
	* Build the navigation config with system pages
	*/
	_buildNavigationConfig(baseConfig) {
		const pages = [...baseConfig.pages];
		if (baseConfig.includeLogin) pages.push(SYSTEM_PAGES.login);
		pages.push(SYSTEM_PAGES.unauthorized);
		pages.push(SYSTEM_PAGES.notFound);
		return {
			...baseConfig,
			pages
		};
	}
	/**
	* Transform navigation pages to route definitions
	*/
	_transformRoutes(navigation) {
		return navigation.pages.map((page) => ({
			name: page.name,
			pattern: page.pattern,
			auth: page.auth,
			authFallback: page.authFallback,
			roles: page.roles
		}));
	}
};
/**
* Global SPA configuration manager instance
*/
var spaConfig = new SpaConfigManager();
//#endregion
export { spaConfig };
